import { useState, useEffect, useCallback } from 'react';
import { Search, Coins, X, Check, Infinity } from 'lucide-react';
import ScootSearchBar from '../components/scoot/ScootSearchBar';
import ScootResultCard from '../components/scoot/ScootResultCard';
import ScootMessageModal from '../components/scoot/ScootMessageModal';
import ScootFilters from '../components/scoot/ScootFilters';
import { useScootSearch } from '../hooks/useScootSearch';
import { ScootFilters as Filters } from '../hooks/useScootQuery';
import { useAuth } from '../contexts/AuthContextSupabase';
import { supabase } from '../lib/supabase';

export default function Scoot() {
  const { currentUser, userProfile } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Filters>({});
  const { results, loading, isLoadingMore, hasMore, error, loadMore } = useScootSearch(searchQuery, filters);
  const [selectedCreator, setSelectedCreator] = useState<any>(null);
  const [creditsBalance, setCreditsBalance] = useState<number>(0);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [recruitedInfluencers, setRecruitedInfluencers] = useState<Set<string>>(new Set());
  const [recruitmentStatus, setRecruitmentStatus] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (userProfile?.agency_id) {
      loadCredits();
      loadRecruitedInfluencers();
    }
  }, [userProfile?.agency_id]);

  const loadCredits = async () => {
    if (!userProfile?.agency_id) return;

    try {
      const { data, error } = await supabase.rpc('get_organization_credits', {
        org_id: userProfile.agency_id,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setCreditsBalance(data[0].balance);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setIsLoadingCredits(false);
    }
  };

  const loadRecruitedInfluencers = async () => {
    if (!currentUser || !userProfile?.agency_id) return;

    try {
      const { data, error } = await supabase
        .from('recruitment_influencers')
        .select('influencer_id, status')
        .eq('agency_id', userProfile.agency_id);

      if (error) throw error;

      const influencerIds = new Set(data?.map(item => item.influencer_id).filter(Boolean) || []);
      const statusMap = new Map(data?.map(item => [item.influencer_id, item.status]) || []);
      setRecruitedInfluencers(influencerIds);
      setRecruitmentStatus(statusMap);
    } catch (error) {
      console.error('Error loading recruited influencers:', error);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleMessageGenerated = () => {
    loadCredits();
    showNotification('Message généré avec succès', 'success');
  };

  const handleScroll = useCallback((e: Event) => {
    const target = e.target as HTMLElement;
    if (!target) return;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollThreshold = target.scrollHeight - 800;
    if (!isLoadingMore && hasMore && scrollPosition >= scrollThreshold) {
      loadMore();
    }
  }, [isLoadingMore, hasMore, loadMore]);

  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll, { passive: true });
      return () => mainElement.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleGenerateMessage = async (creator: any) => {
    // Check credits before opening modal
    if (!userProfile?.agency_id) {
      showNotification('Veuillez vous connecter pour générer un message', 'error');
      return;
    }

    try {
      const { data, error } = await supabase.rpc('get_organization_credits', {
        org_id: userProfile.agency_id,
      });

      if (error) throw error;

      const currentBalance = data?.[0]?.balance || 0;

      if (currentBalance < 1) {
        showNotification(
          `Crédits insuffisants. Cette action nécessite 1 crédit. Solde actuel: ${currentBalance}`,
          'error'
        );
        return;
      }

      // Credits are sufficient, open modal
      setSelectedCreator(creator);
    } catch (error) {
      console.error('Error checking credits:', error);
      showNotification('Erreur lors de la vérification des crédits', 'error');
    }
  };

  const handleAddToRecruitment = async (creator: any) => {
    if (!currentUser || !userProfile?.agency_id) {
      showNotification('Vous devez être connecté pour effectuer cette action', 'error');
      return;
    }

    if (recruitedInfluencers.has(creator.id)) {
      try {
        const { data: existingRecords, error: fetchError } = await supabase
          .from('recruitment_influencers')
          .select('id')
          .eq('agency_id', userProfile.agency_id)
          .eq('influencer_id', creator.id);

        if (fetchError) throw fetchError;

        if (existingRecords && existingRecords.length > 0) {
          const { error: deleteError } = await supabase
            .from('recruitment_influencers')
            .delete()
            .eq('id', existingRecords[0].id);

          if (deleteError) throw deleteError;

          const newSet = new Set(recruitedInfluencers);
          newSet.delete(creator.id);
          setRecruitedInfluencers(newSet);
          const newStatusMap = new Map(recruitmentStatus);
          newStatusMap.delete(creator.id);
          setRecruitmentStatus(newStatusMap);
          showNotification('Influenceur retiré du recrutement', 'success');
        }
      } catch (error) {
        console.error('Error removing influencer from recruitment:', error);
        showNotification('Erreur lors du retrait de l\'influenceur', 'error');
      }
    } else {
      try {
        const { data, error } = await supabase
          .from('recruitment_influencers')
          .insert({
            influencer_id: creator.id,
            agency_id: userProfile.agency_id,
            full_name: creator.display_name || creator.name || creator.username,
            username: creator.username,
            instagram_url: creator.profile_url || `https://instagram.com/${creator.username}`,
            profile_picture: creator.profile_image_url,
            internal_rating: 3,
            tags: creator.category ? [creator.category] : [],
            collaborated_brands: '',
            status: 'spotted',
            created_by: currentUser?.id
          })
          .select();

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        setRecruitedInfluencers(prev => new Set([...prev, creator.id]));
        setRecruitmentStatus(prev => new Map([...prev, [creator.id, 'spotted']]));
        showNotification('Influenceur ajouté au recrutement', 'success');
      } catch (error) {
        console.error('Error adding influencer to recruitment:', error);
        showNotification('Erreur lors de l\'ajout de l\'influenceur', 'error');
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
          <div
            className={`rounded-xl shadow-lg p-4 pr-12 max-w-md border ${
              notification.type === 'success'
                ? 'bg-green-500/20 border-green-500/30 text-green-300'
                : notification.type === 'error'
                ? 'bg-red-500/20 border-red-500/30 text-red-300'
                : 'bg-blue-500/20 border-blue-500/30 text-blue-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {notification.type === 'success' && <Check className="w-5 h-5 text-green-400" />}
                {notification.type === 'error' && <X className="w-5 h-5 text-red-400" />}
                {notification.type === 'info' && <Coins className="w-5 h-5 text-blue-400" />}
              </div>
              <p className="text-sm font-medium flex-1">{notification.message}</p>
              <button
                onClick={() => setNotification(null)}
                className="absolute top-3 right-3 text-white/30 hover:text-white/60 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Search className="h-8 w-8 text-white/60" />
              <div>
                <h1 className="text-3xl font-bold text-white">Scoot</h1>
                <p className="text-white/50">
                  Recherchez et analysez des créateurs pour le recrutement
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-5 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-white/[0.08] flex items-center justify-center">
                  <Coins className="w-5 h-5 text-white/60" />
                </div>
                <div className="text-left">
                  <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Crédits</p>
                  {isLoadingCredits ? (
                    <p className="text-lg font-bold text-white">...</p>
                  ) : (
                    <p className="text-lg font-bold text-white flex items-center justify-center">{creditsBalance >= 999999 ? <Infinity className="w-5 h-5" /> : creditsBalance}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <ScootSearchBar value={searchQuery} onChange={setSearchQuery} />
          </div>
          <ScootFilters filters={filters} onFiltersChange={setFilters} />
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-5 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/[0.08] flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-white/[0.08] rounded w-3/4" />
                      <div className="h-3 bg-white/[0.06] rounded w-1/2" />
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="h-8 bg-white/[0.06] rounded-lg" />
                    <div className="h-8 bg-white/[0.06] rounded-lg" />
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="h-3 bg-white/[0.06] rounded" />
                    <div className="h-3 bg-white/[0.06] rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 inline-block">
                <p className="text-red-400">{error}</p>
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-white/30" />
              </div>
              <p className="text-white text-xl font-medium mb-2">
                {searchQuery && searchQuery.length >= 2
                  ? 'Aucun créateur trouvé pour cette recherche'
                  : 'Aucun créateur disponible'}
              </p>
              <p className="text-white/50">
                {searchQuery && searchQuery.length >= 2
                  ? 'Essayez avec d\'autres termes'
                  : 'Les créateurs apparaitront ici une fois ajoutés'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {results.map((creator) => (
                  <ScootResultCard
                    key={creator.id}
                    creator={creator}
                    onGenerateMessage={() => handleGenerateMessage(creator)}
                    onAddToRecruitment={() => handleAddToRecruitment(creator)}
                    isRecruited={recruitedInfluencers.has(creator.id)}
                    recruitmentStatus={recruitmentStatus.get(creator.id)}
                  />
                ))}
              </div>
              {isLoadingMore && (
                <div className="flex justify-center py-8">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-white/10 border-t-white/60"></div>
                    <span className="text-white/50 font-medium">Chargement...</span>
                  </div>
                </div>
              )}
              {!hasMore && results.length > 0 && (
                <div className="text-center py-8">
                  <p className="text-white/50 font-medium">
                    Tous les créateurs ont été chargés ({results.length} au total)
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {selectedCreator && (
          <ScootMessageModal
            creator={selectedCreator}
            onClose={() => setSelectedCreator(null)}
            onError={(message) => showNotification(message, 'error')}
            onSuccess={() => handleMessageGenerated()}
          />
        )}
      </div>
    </div>
  );
}
